import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TriangleAlert, Send, RefreshCw, Copy } from 'lucide-react';
import { autoLog, reportError } from '@/lib/error-report';

interface Props {
  children: ReactNode;
}

type Phase = 'idle' | 'sending' | 'sent' | 'failed';

interface State {
  error: Error | null;
  phase: Phase;
  code: string;
  description: string;
}

// Класс-компонент (hooks недоступны) → строки хардкодом на ru (partner-webapp ru-only).
// reportCode показываем ПОСЛЕ отправки — тогда он гарантированно в БД. Тихий авто-лог
// краша летит сразу в componentDidCatch.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, phase: 'idle', code: '', description: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
    void autoLog(error, { severity: 'crash', componentStack: info.componentStack ?? undefined }).catch(
      () => {},
    );
  }

  private send = async () => {
    const err = this.state.error;
    if (!err) return;
    this.setState({ phase: 'sending' });
    const res = await reportError(err, {
      kind: 'user_report',
      severity: 'crash',
      description: this.state.description.trim() || undefined,
    });
    this.setState({ phase: res.ok ? 'sent' : 'failed', code: res.reportCode });
  };

  private copyCode = () => {
    navigator.clipboard?.writeText(this.state.code).catch(() => {});
  };

  render() {
    if (!this.state.error) return this.props.children;
    const err = this.state.error;
    const { phase, code, description } = this.state;
    const done = phase === 'sent' || phase === 'failed';

    return (
      <div className="min-h-screen bg-background text-foreground p-6 flex flex-col items-center justify-center gap-5 text-center">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(248,113,113,0.14)' }}
        >
          <TriangleAlert size={30} color="var(--color-danger)" />
        </div>
        <div className="space-y-1">
          <h2 className="text-xl font-bold">Что-то сломалось</h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {err.message || 'Неизвестная ошибка'}
          </p>
        </div>

        {done ? (
          <div className="w-full max-w-sm space-y-4">
            <p className="text-sm text-muted-foreground">
              {phase === 'sent'
                ? 'Спасибо! Разработчики уже разбираются.'
                : 'Не удалось отправить. Сохраните код и назовите его в поддержке.'}
            </p>
            <div className="surface-inset rounded-xl px-4 py-3 text-left">
              <div className="eyebrow mb-1">Код отчёта</div>
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-lg font-bold tracking-wider select-all">{code}</span>
                <button
                  type="button"
                  onClick={this.copyCode}
                  className="flex items-center gap-1 text-xs text-foreground active:scale-95 transition-transform"
                >
                  <Copy size={14} /> Скопировать
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-sm space-y-3">
            <textarea
              value={description}
              onChange={(e) => this.setState({ description: e.target.value })}
              placeholder="Опиши, что случилось (необязательно)"
              rows={3}
              maxLength={2000}
              className="surface-inset w-full rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-white/35 resize-none focus:outline-none"
            />
            <button
              type="button"
              onClick={this.send}
              disabled={phase === 'sending'}
              className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Send size={16} />
              {phase === 'sending' ? 'Отправляю…' : 'Отправить отчёт разработчикам'}
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => window.location.reload()}
          className="flex items-center justify-center gap-2 text-sm font-semibold text-white/60 active:text-white transition-colors"
        >
          <RefreshCw size={15} /> Перезагрузить
        </button>
      </div>
    );
  }
}
